// Call signaling state fed by MQTT (OI-1564): incoming-call ring on
// `omcare/users/<id>/calls/incoming`, lifecycle on `omcare/bus/<chat>/calls/status`.
// `activeCall` is the in-page v2 call this window is currently placing/in.

export type CallStatus =
  | 'RING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REJECTED'
  | 'NO_ANSWER'
  | 'DISCONNECTED';

export const TERMINAL_CALL_STATUSES: CallStatus[] = [
  'COMPLETED',
  'REJECTED',
  'NO_ANSWER',
  'DISCONNECTED',
];

export interface IncomingCall {
  callId: number;
  chatId: string;
  callerCustomerId: number;
  janusUrl: string;
  roomId: number;
  token: string;
}

export interface CallStatusUpdate {
  callId: number;
  chatId: string;
  status: CallStatus;
}

// requesting → (outgoing) REST start in flight; ringing → waiting for peer;
// connecting → Janus negotiating; in_call → media flowing; ended → tearing down.
export type ActiveCallPhase =
  'requesting' | 'ringing' | 'connecting' | 'in_call' | 'ended';

export interface ActiveCall {
  direction: 'outgoing' | 'incoming';
  phase: ActiveCallPhase;
  peerCustomerId: number;
  peerName: string;
  // Janus creds — null on an outgoing call until REST `start` returns.
  callId: number | null;
  chatId: string | null;
  janusUrl: string | null;
  roomId: number | null;
  token: string | null;
  // Terminal bus status that ended the call (for the outcome message).
  endReason: CallStatus | null;
}

export interface CallsStore {
  incomingCall: IncomingCall | null;
  lastStatus: CallStatusUpdate | null;
  activeCall: ActiveCall | null;
}

export enum CallsActionTypes {
  SET_INCOMING_CALL = 'CALLS_SET_INCOMING_CALL',
  CLEAR_INCOMING_CALL = 'CALLS_CLEAR_INCOMING_CALL',
  SET_CALL_STATUS = 'CALLS_SET_CALL_STATUS',
  REQUEST_OUTGOING_CALL = 'CALLS_REQUEST_OUTGOING_CALL',
  ACCEPT_INCOMING_CALL = 'CALLS_ACCEPT_INCOMING_CALL',
  SET_ACTIVE_CALL_CREDS = 'CALLS_SET_ACTIVE_CALL_CREDS',
  SET_ACTIVE_CALL_PHASE = 'CALLS_SET_ACTIVE_CALL_PHASE',
  END_ACTIVE_CALL = 'CALLS_END_ACTIVE_CALL',
  // Handled by the MQTT middleware (not the reducer): per-call bus subscribe.
  SUBSCRIBE_CALL_STATUS = 'CALLS_SUBSCRIBE_CALL_STATUS',
  UNSUBSCRIBE_CALL_STATUS = 'CALLS_UNSUBSCRIBE_CALL_STATUS',
}

export type SetIncomingCallAction = {
  type: CallsActionTypes.SET_INCOMING_CALL;
  payload: IncomingCall;
};

export type ClearIncomingCallAction = {
  type: CallsActionTypes.CLEAR_INCOMING_CALL;
};

export type SetCallStatusAction = {
  type: CallsActionTypes.SET_CALL_STATUS;
  payload: CallStatusUpdate;
};

export type RequestOutgoingCallAction = {
  type: CallsActionTypes.REQUEST_OUTGOING_CALL;
  payload: { peerCustomerId: number; peerName: string };
};

export type AcceptIncomingCallAction = {
  type: CallsActionTypes.ACCEPT_INCOMING_CALL;
  payload: { peerName: string };
};

export type SetActiveCallCredsAction = {
  type: CallsActionTypes.SET_ACTIVE_CALL_CREDS;
  payload: {
    callId: number;
    chatId: string;
    janusUrl: string;
    roomId: number;
    token: string;
  };
};

export type SetActiveCallPhaseAction = {
  type: CallsActionTypes.SET_ACTIVE_CALL_PHASE;
  payload: { phase: ActiveCallPhase };
};

export type EndActiveCallAction = {
  type: CallsActionTypes.END_ACTIVE_CALL;
};

export type SubscribeCallStatusAction = {
  type: CallsActionTypes.SUBSCRIBE_CALL_STATUS;
  payload: { chatId: string };
};

export type UnsubscribeCallStatusAction = {
  type: CallsActionTypes.UNSUBSCRIBE_CALL_STATUS;
  payload: { chatId: string };
};

export type CallsAction =
  | SetIncomingCallAction
  | ClearIncomingCallAction
  | SetCallStatusAction
  | RequestOutgoingCallAction
  | AcceptIncomingCallAction
  | SetActiveCallCredsAction
  | SetActiveCallPhaseAction
  | EndActiveCallAction
  | SubscribeCallStatusAction
  | UnsubscribeCallStatusAction;
